// KÉZI kiadás create/edit — jobb oldali beúszó FormSheet (a support-csapat/fizetési kártya minta).
//
// Ez az űrlap CSAK kézi tételre való: külföldi szállító, nyugta, adószám nélküli számla — ami a NAV bejövő
// adatszolgáltatásában SOHA nem jelenik meg. A NAV-ból importált soron a szerkesztő meg sem nyílik (a sor
// akció-menüje nem kínálja), a szerver pedig 422-vel zárja az utat.
//
// A BRUTTÓ SZÁMÍTOTT, nem beírt mező (`net + vat`): egy harmadik, kézzel írt szám garantáltan elcsúszna a
// másik kettőtől, és a kimutatás onnantól két különböző igazságot mutatna.

import { addDecimals } from '@atlas/shared';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  ComboboxField,
  DateField,
  SelectField,
  SwitchField,
  TextareaField,
  TextField,
} from '@/components/shared/form-fields';
import { FormSheet } from '@/components/shared/form-sheet';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { Text } from '@/components/ui/text';
import { employeeSearch } from '@/features/employees/search';
import { categoryOptions, EXPENSE_ISSUERS, expenseKeys } from '@/features/expenses/api';
import {
  buildExpenseResolver,
  DERIVED_MONTH,
  EMPTY_EXPENSE_FORM,
  expenseFormCopyOf,
  expenseFormFrom,
  NO_CATEGORY,
  toExpenseBody,
  type ExpenseFormValues,
} from '@/features/expenses/form';
import { api } from '@/lib/api';
import { formatMoney } from '@/lib/format-money';
import { formatMonthLabel } from '@/lib/month';
import { monthOptions } from '@/components/expenses/expense-month-cell';
import { CURRENCIES } from '@/lib/pricing-form';
import { useEntityMutation } from '@/lib/use-entity-mutation';

import type { ExpenseCategoryRow, ExpenseRow } from '@atlas/contracts';

export interface ExpenseFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `null` → FELVÉTEL; egyébként a szerkesztendő (kézi) kiadás — OCC `version` + előtöltés. */
  expense: ExpenseRow | null;
  /**
   * MÁSOLÁS: FELVÉTEL (`create`), egy meglévő kézi kiadás adataival előtöltve (számlaszám nélkül — lásd
   * `expenseFormCopyOf`). Csak `expense === null` mellett hat: a szerkesztés erősebb.
   */
  copyFrom?: ExpenseRow | null;
  categories: readonly ExpenseCategoryRow[];
  /**
   * MEMÓRIABELI szerkesztés (a tömeges import sor-javítása): előtöltés az `expense` HELYETT, és a mentés
   * NEM a szervernek megy, hanem ennek a visszahívásnak.
   *
   * MIÉRT ÍGY, és miért nem külön űrlap: a javítandó sor ugyanaz a kiadás, ugyanazokkal a szabályokkal —
   * egy második űrlap garantáltan elcsúszna ettől, és az importon át más adat jutna be, mint kézzel.
   */
  draft?: {
    values: ExpenseFormValues;
    /** A már feloldott munkatárs CÍMKÉJE (a választó enélkül üresen állna a keresés lefutásáig). */
    employeeLabel: string | null;
    title: string;
    description: string;
    onSubmit: (values: ExpenseFormValues) => void;
  };
}

export function ExpenseFormSheet({
  open,
  onOpenChange,
  expense,
  copyFrom = null,
  categories,
  draft,
}: ExpenseFormSheetProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const isEdit = expense !== null;
  const copySource = isEdit ? null : copyFrom;
  /** A sor, amiből az űrlap előtölt (szerkesztett VAGY másolt) — a munkatárs-címke ebből jön. */
  const source = expense ?? copySource;
  // SZERKESZTÉSNÉL a már kiválasztott munkatárs CÍMKÉJE a sorról jön — enélkül a választó üresen állna,
  // amíg a keresés le nem fut (a címtárat nem töltjük be előre).
  const initialEmployee =
    draft !== undefined
      ? draft.values.employeeIdentityId !== '' && draft.employeeLabel !== null
        ? { id: draft.values.employeeIdentityId, label: draft.employeeLabel }
        : null
      : source?.employeeIdentityId != null && source.employeeName !== null
        ? { id: source.employeeIdentityId, label: source.employeeName }
        : null;

  const form = useForm<ExpenseFormValues>({
    resolver: buildExpenseResolver(t),
    mode: 'onTouched',
    defaultValues: EMPTY_EXPENSE_FORM,
  });
  const { reset, handleSubmit, setError, watch } = form;

  useEffect(() => {
    if (draft !== undefined) reset(draft.values);
    else if (expense) reset(expenseFormFrom(expense));
    else reset(copySource ? expenseFormCopyOf(copySource) : EMPTY_EXPENSE_FORM);
    // A `draft.values` ÚJ objektum minden renderen; a sor AZONOSÍTÓJA (a cím) a stabil jel, hogy MÁSIK
    // sort szerkesztünk — enélkül a reset minden billentyűleütés után visszaállítaná a mezőket.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expense, copySource, draft?.title, reset]);

  const net = watch('netAmount');
  const vat = watch('vatAmount');
  const currency = watch('currency');
  // A vonatkozási hónap ABLAKA a felvitt BIZONYLAT dátuma körül áll (teljesítés, híján kiállítás) — nem a
  // nézett hónap körül: egy régi számla rögzítésekor a nézett hónap irreleváns. Üres dátumnál nincs horgony,
  // ilyenkor csak a „Bizonylat szerint" tétel áll a listában (az űrlap amúgy is kéri a kiállítás dátumát).
  const issueDate = watch('issueDate');
  const fulfillmentDate = watch('fulfillmentDate');
  const gross = addDecimals(net, vat);
  // NINCS SZÁMLA: a számlaszám elrejtve és elhagyható, a dátum CÍMKÉJE pedig „a költség dátuma" — ez a mező
  // ilyenkor sem opcionális (a vonatkozási hónap ebből származik), csak már nem egy bizonylatra mutat.
  const noBill = watch('noBill');
  // A MUNKATÁRS-választó CSAK BÉR TÍPUSÚ TÉTELNÉL áll: a jelölés a kiválasztott kategórián ül (nem
  // öröklődik). A MÁR BEÁLLÍTOTT értéket viszont sosem rejtjük el — egy kategória-váltás nem tüntetheti el
  // szem elől a kötést, amit valaki megadott (a szerver sem nullázza).
  const categoryId = watch('categoryId');
  const employeeIdentityId = watch('employeeIdentityId');
  /**
   * A HÓNAP-ABLAK a bizonylat dátuma köré áll — DE a MÁR BEÁLLÍTOTT érték akkor is benne kell legyen, ha
   * kiesik belőle (ugyanaz a „szelep", mint a lista sor-cellájában). Enélkül a legördülő a saját értékét nem
   * tudná megmutatni: üres dátumnál (import-javítás egy rossz dátumú soron) az ablak ÜRES, és a kitöltött
   * hónap némán eltűnt volna — mérve.
   */
  const accountingMonth = watch('accountingMonth');
  const monthWindow = monthOptions(
    (fulfillmentDate === '' ? issueDate : fulfillmentDate).slice(0, 7),
  );
  if (accountingMonth !== DERIVED_MONTH && !monthWindow.includes(accountingMonth)) {
    monthWindow.push(accountingMonth);
    monthWindow.sort().reverse();
  }

  const employeeAllowed =
    categories.find((c) => c.id === categoryId)?.requiresEmployee === true ||
    employeeIdentityId !== '';

  const mutation = useEntityMutation<ExpenseFormValues>({
    mutationFn: (values) =>
      isEdit
        ? api.expenses.update.mutation({
            params: { id: expense.id },
            body: toExpenseBody(values),
            headers: { 'if-match': String(expense.version) },
          })
        : api.expenses.create.mutation({ body: toExpenseBody(values) }),
    // A `all` prefix EGY sorban éri el a listát, az összegzést és a kategória-fát — egy írás
    // mindhármat elavulttá teszi (a darabszámok és a havi összeg is mozdul).
    invalidateKeys: [expenseKeys.all],
    onFieldErrors: (field, messages) => {
      if (field in EMPTY_EXPENSE_FORM) {
        setError(field as keyof ExpenseFormValues, { message: messages.join(' ') });
      }
    },
    messages: {
      success: isEdit ? t('toast:expenses.updated') : t('toast:expenses.created'),
      // A 409 a create-en DUPLIKÁTUM (adószám + számlaszám), a szerkesztésen OCC-ütközés — a szerver
      // mindkettőt 409-cel adja, de a felhasználónak MÁS a teendője.
      conflict: isEdit ? t('toast:expenses.conflict') : t('toast:expenses.duplicate'),
      forbidden: t('toast:expenses.forbidden'),
      validation: t('toast:expenses.validation'),
      error: t('toast:expenses.error'),
      unavailable: t('toast:expenses.unavailable'),
    },
  });

  const onSubmit = handleSubmit((values) => {
    if (draft !== undefined) {
      draft.onSubmit(values);
      onOpenChange(false);
      return;
    }
    mutation.mutate(values, { onSuccess: () => onOpenChange(false) });
  });

  return (
    <FormSheet
      open={open}
      onOpenChange={(o) => {
        if (!o && draft === undefined) reset(EMPTY_EXPENSE_FORM);
        onOpenChange(o);
      }}
      title={
        draft?.title ??
        (isEdit
          ? t('expenses.form.editTitle')
          : copySource
            ? t('expenses.form.copyTitle')
            : t('expenses.form.createTitle'))
      }
      description={
        draft?.description ??
        (isEdit
          ? expense.supplierName
          : copySource
            ? t('expenses.form.copyDesc', { supplier: copySource.supplierName })
            : t('expenses.form.createDesc'))
      }
      onSubmit={onSubmit}
      busy={mutation.isPending}
      footer={
        <>
          <Button
            variant="outline"
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="brand" disabled={mutation.isPending}>
            {mutation.isPending ? t('common.saving') : t('common.save')}
          </Button>
        </>
      }
    >
      <Form {...form}>
        {/* MELYIK CÉGÜNK költsége. ELÖL áll, mert a sor hovatartozását dönti el: két külön adózó, két külön
            könyvelés — a NAV-soron ezt az import tölti ki, a kézin a kezelő. */}
        <SelectField<ExpenseFormValues>
          name="issuerKey"
          label={t('expenses.fields.issuer')}
          hint={t('expenses.fields.issuerHint')}
          options={EXPENSE_ISSUERS.map((k) => ({
            value: k,
            label: t(`invoices.issuerLabel.${k}`, { defaultValue: k }),
          }))}
        />
        <TextField<ExpenseFormValues>
          name="supplierName"
          label={t('expenses.fields.supplierName')}
          required
        />
        <TextField<ExpenseFormValues>
          name="supplierTaxNumber"
          label={t('expenses.fields.supplierTaxNumber')}
          hint={t('expenses.fields.supplierTaxNumberHint')}
        />
        {/* NINCS SZÁMLA — nyugta, bizonylat nélküli költség. Nem külön tárolt mező: a szerveren a hiányzó
            számlaszám MAGA a jelzés (l. `toExpenseBody`). */}
        <SwitchField<ExpenseFormValues>
          name="noBill"
          label={t('expenses.fields.noBill')}
          hint={t('expenses.fields.noBillHint')}
        />
        {!noBill && (
          <TextField<ExpenseFormValues>
            name="invoiceNumber"
            label={t('expenses.fields.invoiceNumber')}
            required
          />
        )}

        <div className="grid gap-4 sm:grid-cols-3">
          <DateField<ExpenseFormValues>
            name="issueDate"
            label={noBill ? t('expenses.fields.costDate') : t('expenses.fields.issueDate')}
            required
          />
          <DateField<ExpenseFormValues>
            name="fulfillmentDate"
            label={t('expenses.fields.fulfillmentDate')}
          />
          <DateField<ExpenseFormValues> name="dueDate" label={t('expenses.fields.dueDate')} />
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <TextField<ExpenseFormValues>
            name="netAmount"
            label={t('expenses.fields.netAmount')}
            inputMode="decimal"
            required
          />
          <TextField<ExpenseFormValues>
            name="vatAmount"
            label={t('expenses.fields.vatAmount')}
            inputMode="decimal"
            required
          />
          <SelectField<ExpenseFormValues>
            name="currency"
            label={t('expenses.fields.currency')}
            options={CURRENCIES.map((c) => ({ value: c, label: c }))}
            required
          />
        </div>

        {/* A SZÁMÍTOTT bruttó — nem mező, hanem a nettó + áfa kiírása (lásd a fájl fejlécét). */}
        <div className="bg-sunken/40 flex items-center justify-between rounded-lg px-3 py-2">
          <Text variant="secondary" as="span" className="text-xs">
            {t('expenses.fields.grossAmount')}
          </Text>
          <span className="text-foreground text-sm font-semibold tabular-nums">
            {formatMoney(gross, currency, locale)}
          </span>
        </div>

        <SelectField<ExpenseFormValues>
          name="categoryId"
          label={t('expenses.fields.category')}
          options={[
            { value: NO_CATEGORY, label: t('expenses.uncategorized') },
            ...categoryOptions(categories, locale),
          ]}
        />
        {/* MUNKATÁRS — keresős címtár-választó (`/employees`), mert a lista hosszú és nem a lap adata.
            CSAK BÉR TÍPUSÚ TÉTELNÉL áll (l. `employeeAllowed`), és ott is opcionális. */}
        {employeeAllowed && (
          <ComboboxField<ExpenseFormValues>
            name="employeeIdentityId"
            label={t('expenses.employee')}
            hint={t('expenses.employeeHint')}
            search={employeeSearch(locale)}
            placeholder={t('expenses.employeeNone')}
            {...(initialEmployee ? { initialOption: initialEmployee } : {})}
          />
        )}
        {/* VONATKOZÁSI HÓNAP már a felvitelkor (user-kérés): ha a kezelő tudja, hogy a tétel korábbi hónapot
            terhel, ne kelljen utólag megkeresnie a listában. Az alap „Bizonylat szerint" — ilyenkor a hónap
            a megadott dátumokból származik, pontosan úgy, mint eddig. A hónap-ablak a KIÁLLÍTÁS dátuma
            körül áll (a felvitt bizonylaté), nem a nézett hónap körül: egy régi számla rögzítésekor a
            nézett hónap irreleváns. */}
        <SelectField<ExpenseFormValues>
          name="accountingMonth"
          label={t('expenses.fields.accountingMonth')}
          hint={t('expenses.fields.accountingMonthHint')}
          options={[
            { value: DERIVED_MONTH, label: t('expenses.month.derivedOption') },
            ...monthWindow.map((m) => ({ value: m, label: formatMonthLabel(m, locale) })),
          ]}
        />
        <TextareaField<ExpenseFormValues> name="note" label={t('expenses.fields.note')} rows={2} />
      </Form>
    </FormSheet>
  );
}
